<script setup>
import { ref, computed, watch, onMounted, isVNode } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useEventListener } from '@vueuse/core';
import { SidebarMenuSubButton } from './index';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  label: { type: String, required: true },
  icon: { type: [String, Object, Function], default: null },
  children: { type: Array, required: true },
  isParentExpanded: { type: Boolean, default: false },
});

const route = useRoute();
const router = useRouter();
const isExpanded = ref(false);
const scrollableContainer = ref(null);
const scrollEnd = ref(false);

const isActive = item => {
  if (!item.to) return false;
  const itemPath = resolvePath(item.to);
  if (route.path === itemPath) return true;
  if (item.activeOn && item.activeOn.includes(route.name)) return true;
  return false;
};

const hasActiveChild = computed(() => {
  if (!props.children) return false;
  return props.children.some(child => isActive(child));
});

const activeChild = computed(() => {
  return props.children.find(child => isActive(child));
});

const resolvePath = to => {
  if (typeof to === 'string') return to;
  try {
    return router.resolve(to).path;
  } catch (e) {
    return '';
  }
};

const shouldRenderComponent = item => {
  return typeof item.component === 'function' || isVNode(item.component);
};

const isScrollable = computed(() => {
  return props.children.length > 7;
});

// Surveiller le scroll
useEventListener(scrollableContainer, 'scroll', () => {
  if (!scrollableContainer.value) return;
  const { scrollHeight, scrollTop, clientHeight } = scrollableContainer.value;
  scrollEnd.value = scrollHeight - scrollTop <= clientHeight + 1;
});

// Auto-expand si enfant actif
onMounted(() => {
  if (hasActiveChild.value) {
    isExpanded.value = true;
  }
});

watch(
  () => route.path,
  () => {
    if (hasActiveChild.value) {
      isExpanded.value = true;
    }
  }
);
</script>

<template>
  <div v-if="props.children.length > 0">
    <!-- Header du sous-groupe -->
    <Transition name="fade-slide">
      <div
        v-if="isParentExpanded"
        class="flex items-center gap-2.5 px-3.5 py-2 mt-1 mb-0.5 rounded-md min-h-[36px] text-n-slate-11 dark:text-n-slate-10 select-none transition-all duration-150 cursor-pointer hover:bg-n-blue-2 dark:hover:bg-n-blue-3 hover:border-n-blue-6 min-w-0 max-w-full overflow-hidden border border-transparent"
        @click="isExpanded = !isExpanded"
      >
        <Icon
          v-if="icon"
          :icon="icon"
          class="size-[18px] text-n-slate-9 dark:text-n-slate-10 flex-shrink-0"
        />
        <span
          class="text-[13px] font-semibold leading-6 flex-1 uppercase tracking-wide text-left truncate min-w-0 py-0.5"
        >
          {{ label }}
        </span>
        <Icon
          icon="i-lucide-chevron-down"
          class="size-4 flex-shrink-0 transition-transform duration-200"
          :class="{
            'rotate-0': isExpanded || hasActiveChild,
            '-rotate-90': !isExpanded && !hasActiveChild,
          }"
        />
      </div>
    </Transition>

    <!-- Liste des items -->
    <Transition name="fade-slide">
      <div
        v-if="isParentExpanded && (isExpanded || hasActiveChild)"
        class="relative group min-w-0 max-w-full overflow-hidden"
      >
        <div
          ref="scrollableContainer"
          class="transition-all duration-300 ease-out min-w-0 max-w-full"
          :class="{
            'max-h-[calc(14rem+16px)] overflow-y-scroll no-scrollbar':
              isScrollable,
          }"
        >
          <div
            v-for="child in children"
            v-show="isExpanded || activeChild?.name === child.name"
            :key="child.name"
            class="min-w-0 max-w-full overflow-hidden relative ltr:pl-0 rtl:pr-0 rtl:mr-0 ltr:ml-0 py-1 sidebar-sub-item"
          >
            <SidebarMenuSubButton
              v-if="child.to"
              :is-active="isActive(child)"
              as-child
            >
              <router-link
                :to="child.to"
                :data-active="isActive(child)"
                class="flex min-h-[36px] items-center gap-2.5 pl-3.5 pr-0 py-1.5 rounded-md w-full min-w-0 group will-change-auto transition-all duration-150 relative border border-transparent"
                :class="{
                  'text-n-sidebar-text active bg-n-sidebar-subitem-active':
                    isActive(child),
                  'text-n-sidebar-text hover:bg-n-sidebar-item-hover':
                    !isActive(child),
                }"
                :style="
                  isActive(child) ? { zIndex: 1, borderRadius: '0.375rem' } : {}
                "
              >
                <!-- Rendu du composant custom ou standard -->
                <template v-if="shouldRenderComponent(child)">
                  <component
                    :is="
                      child.component({
                        label: child.label,
                        active: isActive(child),
                        icon: child.icon,
                      })
                    "
                  />
                </template>
                <template v-else>
                  <Icon
                    v-if="child.icon"
                    :icon="child.icon"
                    class="size-[18px] inline-block transition-colors duration-200 flex-shrink-0 text-n-sidebar-indicator"
                  />
                  <div
                    class="flex-1 truncate min-w-0 text-[15px] transition-all duration-200 py-0.5"
                  >
                    {{ child.label }}
                  </div>
                </template>
              </router-link>
            </SidebarMenuSubButton>
          </div>
        </div>

        <!-- Indicateur de scroll -->
        <Transition name="fade">
          <div
            v-if="isScrollable && (isExpanded || hasActiveChild) && !scrollEnd"
            class="absolute bg-gradient-to-t from-n-solid-2 dark:from-n-solid-1 w-full h-12 to-transparent -bottom-1 pointer-events-none flex items-end justify-end px-2"
          >
            <svg
              width="16"
              height="24"
              viewBox="0 0 16 24"
              fill="none"
              class="text-n-slate-9 opacity-50 transition-opacity duration-200"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 4L8 8L12 4"
                stroke="currentColor"
                opacity="0.5"
                stroke-width="1.33333"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M4 10L8 14L12 10"
                stroke="currentColor"
                opacity="0.75"
                stroke-width="1.33333"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M4 16L8 20L12 16"
                stroke="currentColor"
                stroke-width="1.33333"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </Transition>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
/* Transitions ultra-fluides */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: opacity;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition:
    opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: opacity, transform;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px) translateZ(0);
}

.sidebar-sub-item::before {
  content: '';
  position: absolute;
  width: 2px;
  height: 0;
  left: -0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background-color: var(--sidebar-indicator);
  border-radius: 2px;
  transition: height 0.2s ease-out;
}

.sidebar-sub-item:has([data-active='true'])::before,
.sidebar-sub-item:has(.active)::before {
  height: 70%;
  top: 50%;
  transform: translateY(-50%);
}

/* RTL support */
#app[dir='rtl'] .sidebar-sub-item::before {
  left: auto;
  right: -0.5rem;
}
</style>
